import { CartPriceType } from 'components/CartWizard/helper';
import {
  Asset,
  BaseType,
  Bubble,
  Id,
  Machine,
  MachineBusinessUnit,
  Paginated,
  Resource,
  WithId,
  WithName,
  WithQuantity
} from 'types';
import { PartNode } from 'types/machine-management';

export interface PartsByIdsArgs extends BaseType {
  uuids?: string[];
  subParts?: boolean;
  flagAssemblies?: boolean;
  includeResources?: boolean;
  onlyImages?: boolean;
  machineId?: string;
  sku?: string;
  businessUnitId?: string;
  include_bom?: boolean;
  currentSku?: string;
}

export interface PartTransformResponse {
  price: string;
  stock: string;
  leadTime: string;
}
export interface GetProductsArgs extends BaseType {
  productIds?: string[];
  includeAssets?: boolean;
  machineUuid?: string;
  priceUnit?: string;
  productTagIds?: string[];
  stockStatusRequire?: string;
}

export interface ProductByIdWithMachineArgs extends BaseType {
  id?: string;
  machineUuid?: string;
  priceUnit?: string;
  isPurchasable?: boolean;
}

export interface GetProductTagsArgs extends BaseType {
  machineId: string;
}

export interface PartsByMachineIdArgs extends BaseType, WithId {
  page?: number;
  itemsPerPage?: number;
  searchQuery?: string;
  startNodeSku?: string;
}

export enum StockError {
  MISSING = 'missing',
  UNREASONABLE = 'unreasonable',
  NONE = 'none'
}

export enum LeadTimeError {
  MISSING = 'missing',
  UNREASONABLE = 'unreasonable',
  NONE = 'none'
}

export enum PriceError {
  MISSING = 'missing',
  UNREASONABLE = 'unreasonable',
  NONE = 'none'
}

export interface PurchasableDetails extends BaseType {
  sku: string;
  description: string;
  manualDescription?: string;
  multilingualDesc?: { [key: string]: string };
  alternateSku?: string;
  businessUnit?: MachineBusinessUnit;
  leadTime: number | string; // number could be hours, seconds, whatever makes sense
  price?: number;
  priceUnit?: string; // eg USD
  custPrice?: number;
  custNum?: string;
  stock: number;
  assets?: Resource[] | Asset[];
  stockError?: StockError;
  priceError?: PriceError;
  leadTimeError?: LeadTimeError;
  siteRef?: string; // inventory location
  isObsolete?: boolean;
  compatibility?: Machine[];
  isPurchasable?: boolean;
  alternateProduct?: Product;
  overview?: string; // This is for PDP, but not sure we ever will get this data
  sfProductId?: string;
  stockCode?: string;
  quantity?: number;
  isPriceMissing?: boolean;
  urgent?: boolean;
  tentativeDeliveryDate?: string;
  subTotal?: string;
  showExactStock?: boolean; // This is for parts table, to show exact stock
}

export interface Product extends WithId, PurchasableDetails {}

export interface PurchasableItem extends PurchasableDetails {
  productId?: string;
}

interface Parts {
  part_id: string;
  product: string;
  sku: string;
  stock: string;
  avgleadtime: string;
  quantity: number | undefined;
}
export interface PriceRequestParams extends BaseType {
  parts: Parts[];
  comments: string;
}

export interface Part extends BaseType, WithId, PurchasableItem {
  categories?: string[];
  parts?: Part[];
  customerPartId?: string;
  isAssembly?: boolean;
  machineId?: string;
  bom?: PartNode[];
}
export interface PartFilterRequest {
  option: string;
  offset: number;
  limit?: number;
  machineId?: string;
}
export interface SavedPart extends Part {
  id: string;
  sku: string;
  description: string;
  price?: number;
  quantity?: number;
  leadTime: number;
  priceUnit?: string;
  isPurchaseable?: boolean;
}
export interface ServiceReport extends WithId, BaseType {
  pdfUrl?: string;
  createdAt?: string;
  machineId?: string;
  documentPath?: string;
  machineName?: string;
}
export interface ServiceReportParams extends Paginated {
  machineIds: Id[];
  includeTasks?: boolean;
  includeProductIds?: boolean;
  creator?: string;
}
export type SavedProductInput = {
  productId: string;
  quantity: number;
  userId?: string;
  priceUnit?: string;
};

export type PartDetail = {
  productId?: string;
  machineId?: string;
  priceUnit?: string;
  partFlyout?: boolean;
  [key: string]: unknown;
};

export interface PartDataWithKey extends Part, Product {
  key: string;
  isPriceMissing: boolean;
}

export interface AlternateRow extends PartDataWithKey {
  rowType: 'alternate';
}

export type TableRow = AlternateRow | PartDataWithKey;

export interface OrderDetails extends WithQuantity {
  firstName: string;
  lastName: string;
  billingAddress: string;
  shippingAddress: string;
  creator: string;
  approver: string;
}

export type OrderBillee = {
  firstName: string;
  lastName: string;
};

export type OrderAddress = {
  streetAddress: string;
  suiteNumber?: string;
  city: string;
  state?: string;
  postalCode: string;
  country: string;
};

export interface ItemOrder extends WithId, WithQuantity, WithName, BaseType {
  machineId?: string;
  description?: string;
  machineDescription?: string;
  currency?: string | null;
  urgent?: boolean;
  procare_allowance?: boolean;
  customerNumber?: string;
  sku?: string;
  unitPrice?: number;
  siteRef?: string;
  isTwoWayOrder?: boolean;
  leadTime?: number;
  stock?: number;
  tentativeDeliveryDate?: string;
  orderStatus?: string;
}

export interface BillTo extends OrderBillee, OrderAddress {}

export interface OrderInput extends BaseType {
  billTo: BillTo;
  shipTo: OrderAddress;
  creator: string;
  approver: string;
  orders: ItemOrder[];
  unpurchasableItems?: ItemOrder[];
  customerPoNumber?: string;
  sfCartId?: string;
  sfAccountId?: string;
  allowPartialShipments?: boolean;
  requestedDate?: string;
}

export interface OrderInputWithSFFlag extends OrderInput, OrderInputWithPriceInfo {
  sfEnabled?: boolean;
  lang?: string;
  region?: string;
}

export interface OrderInputWithPriceInfo {
  subtotal?: number;
  discount?: number;
  tax?: number;
  total?: number;
  extraFee?: number;
  legalizationCost?: number;
  shipping?: number;
  isTwoWayOrder?: boolean;
  erpOrderNumber?: string;
  currencyCode?: string;
  parts?: Array<{
    discountAmount: number;
    discountPercentage: number;
    idOrdersItem: string;
    prosealPartNumber: string;
    quantity: number;
    totalPriceExTax: number;
    totalValueExTax: number;
    unitValueExTax: number;
  }>;
}

export interface OrderInputWithFile extends OrderInputWithSFFlag {
  files?: File[];
}

export interface OrderResponse extends BaseType, WithId {
  url?: string;
  sfPoId?: string;
  orderId?: string;
  orderNumber?: string;
  quoteRequestId?: string;
  orderPlaced?: boolean;
  orderType?: CartPriceType;
}
export interface OrderCalculationResponse extends BaseType {
  model?: {
    currencyCode?: string;
    description?: string;
    idOrder?: string;
    prosealOrderNumber?: string;
    orderStatus?: string;
    dispatchValueExTax?: number;
    salesPriceExTax?: number;
    salesPriceTaxAmount?: number;
    dispatches?: string[];
    parts?: Array<{
      discountAmount: number;
      discountPercentage: number;
      idOrdersItem: string;
      prosealPartNumber: string;
      quantity: number;
      totalPriceExTax: number;
      totalValueExTax: number;
      unitValueExTax: number;
    }>;
  };
  errors?: [];
  warnings?: [];
}
export interface ConfirmOrderInput extends BaseType, WithId {
  email: string;
  status: boolean;
  sfPoId?: string;
  requestedDate?: string;
}

export interface ConfirmOrderInputWithSFFlag extends ConfirmOrderInput {
  sfEnabled?: boolean;
  sfAccountId?: string;
  erpOrderNumber?: string;
  isTwoWayOrder?: boolean;
}

export interface ProductTag extends BaseType, WithId {
  tagName: string;
  createdDate: Date;
}
export interface ModifiedPart extends BaseType {
  [key: string]: string;
}

export interface DiagramBubbleAction {
  (
    marker: Bubble,
    event: React.MouseEvent,
    bubbleBaseRef: React.MutableRefObject<null>,
    part?: Part,
    diagram?: Resource,
    bubbledEditId?: Id,
    inEditMode?: boolean
  ): void;
}

export interface DiagramAction {
  (event: React.MouseEvent, width?: number, height?: number, diagram?: Resource): void;
}
